import {
    getEditorVersionFilter,
    isInsideZyVersion,
    normalizeSelectedZyVersionFilter,
    type SelectedZyVersionFilter,
} from '@/lib/zy-tiptap-utils';
import { normalizeVersion } from '@/lib/zy-tiptap-utils-core';
import { Editor, Node, mergeAttributes } from '@tiptap/core';
import { Fragment, NodeRange, type Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { findWrapping } from '@tiptap/pm/transform';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ZyFctNameInline } from './zyfctname-inline-extension';
import { ZyVersionBlockNodeView } from './zyversion-block-node-view';
import { ZyVersionInlineNodeView } from './zyversion-inline-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyVersion: {
            insertZyVersionBlock: (attrs?: { version?: string }) => ReturnType;
            insertZyVersionInline: (attrs?: { version?: string; pos?: number }) => ReturnType;
            insertZyDataMember: () => ReturnType;
            insertZyFctName: () => ReturnType;
            insertZyFunction: () => ReturnType;
            toggleZyVersion: (attrs?: { version?: string }) => ReturnType;
            toggleOrInsertZyVersion: (attrs?: { version?: string }) => ReturnType;
            setVersionFilter: (versionFilter: SelectedZyVersionFilter) => ReturnType;
        };
    }
}

interface BlockSegment {
    blockFrom: number;
    blockTo: number;
    selFrom: number;
    selTo: number;
    full: boolean;
}

interface WrapAction {
    type: 'inline' | 'block';
    from: number;
    to: number;
    selFrom?: number;
    selTo?: number;
}

const versionAttribute = {
    default: '',
    parseHTML: (element: HTMLElement) => normalizeVersion(element.getAttribute('version') ?? ''),
    renderHTML: (attributes: Record<string, unknown>) => (attributes.version ? { version: attributes.version } : {}),
};

export const ZyVersionBlockHeadless = Node.create({
    name: 'zyVersionBlock',

    content: '(listItem | block)*',

    group: 'block',

    isolating: true,

    draggable: true,

    addAttributes() {
        return { version: versionAttribute };
    },

    parseHTML() {
        return [
            {
                tag: 'zyVersion',
                getAttrs: element => {
                    if (!(element instanceof HTMLElement)) return false;
                    return { version: element.getAttribute('version') ?? '' };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyVersion', mergeAttributes(HTMLAttributes), 0];
    },

    addCommands() {
        return {
            insertZyVersionBlock:
                (attrs = {}) =>
                ({ editor, commands }) => {
                    return commands.insertContent({
                        type: 'zyVersionBlock',
                        attrs: { version: getToggleOrInsertVersion(editor, attrs) },
                        content: [{ type: 'paragraph' }],
                    });
                },

            toggleOrInsertZyVersion:
                (attrs = {}) =>
                ({ editor, commands }) => {
                    const { empty, from } = editor.state.selection;

                    if (!empty || isInsideZyVersion(editor)) {
                        return commands.toggleZyVersion(attrs);
                    }

                    const $pos = editor.state.doc.resolve(from);
                    const parent = $pos.parent;
                    if (parent.isTextblock && parent.textContent.length > 0) {
                        return commands.insertZyVersionInline(attrs);
                    }

                    return commands.insertZyVersionBlock(attrs);
                },
        };
    },
});

export const ZyVersionInlineHeadless = Node.create({
    name: 'zyVersionInline',

    group: 'inline',

    inline: true,

    content: 'inline+',

    addAttributes() {
        return { version: versionAttribute };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-zy-version-inline]',
                getAttrs: element => {
                    if (!(element instanceof HTMLElement)) return false;
                    return { version: element.getAttribute('version') ?? '' };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(HTMLAttributes), 0];
    },

    addExtensions() {
        return [ZyFctNameInline];
    },

    addCommands() {
        return {
            insertZyVersionInline:
                (attrs = {}) =>
                ({ editor, tr, dispatch }) => {
                    const from = attrs.pos ?? editor.state.selection.from;
                    const version = getToggleOrInsertVersion(editor, attrs);
                    const node = this.type.create({ version }, [editor.schema.text(' ')]);
                    const space = editor.schema.text(' ');
                    tr.replaceWith(from, from, [node, space]);

                    // Select the placeholder text inside the inline node so the user can type over it.
                    const contentStart = from + 1;
                    const contentEnd = from + node.nodeSize - 1;
                    tr.setSelection(TextSelection.create(tr.doc, contentStart, contentEnd));
                    if (dispatch) {
                        dispatch(tr);
                        editor.view.focus();
                    }
                    return true;
                },

            insertZyDataMember:
                () =>
                ({ editor, tr, dispatch }) => {
                    const from = editor.state.selection.from;
                    const javaNode = this.type.create({ version: 'java' }, [editor.schema.text('data member')]);
                    const cppNode = this.type.create({ version: 'c,cpp' }, [editor.schema.text('field')]);

                    tr.replaceWith(from, from, [javaNode, cppNode]);

                    if (dispatch) {
                        dispatch(tr);
                        editor.view.focus();
                    }

                    return true;
                },

            insertZyFunction:
                () =>
                ({ editor, tr, dispatch }) => {
                    const from = editor.state.selection.from;
                    const javaNode = this.type.create({ version: 'java,javaeo' }, [editor.schema.text('method')]);
                    const cppNode = this.type.create({ version: 'c,cpp' }, [editor.schema.text('function')]);

                    tr.replaceWith(from, from, [javaNode, cppNode]);

                    if (dispatch) {
                        dispatch(tr);
                        editor.view.focus();
                    }

                    return true;
                },

            toggleZyVersion:
                (attrs = {}) =>
                ({ editor, tr, dispatch }) => {
                    const state = editor.state;
                    const { from, to, empty } = state.selection;
                    const doc = state.doc;
                    const version = getToggleOrInsertVersion(editor, attrs);
                    const inlineType = state.schema.nodes.zyVersionInline;
                    const blockType = state.schema.nodes.zyVersionBlock;

                    // --- Detect existing zyVersion nodes ---
                    const zyVersionNodes: { pos: number; node: PMNode }[] = [];

                    if (empty) {
                        const $pos = doc.resolve(from);
                        for (let d = $pos.depth; d > 0; d--) {
                            const node = $pos.node(d);
                            if (node.type === inlineType || node.type === blockType) {
                                zyVersionNodes.push({ pos: $pos.before(d), node });
                                break;
                            }
                        }
                    } else {
                        doc.nodesBetween(from, to, (node, pos) => {
                            if (node.type === inlineType || node.type === blockType) {
                                zyVersionNodes.push({ pos, node });
                                return false;
                            }
                        });
                    }

                    // --- UNWRAP ---
                    if (zyVersionNodes.length > 0) {
                        if (!dispatch) return true;
                        zyVersionNodes.sort((a, b) => b.pos - a.pos);
                        for (const { pos, node } of zyVersionNodes) {
                            tr.replaceWith(pos, pos + node.nodeSize, node.content);
                        }
                        dispatch(tr);
                        return true;
                    }

                    // --- WRAP ---
                    if (empty) return false;

                    if (state.selection instanceof NodeSelection) {
                        if (!dispatch) return true;
                        const sel = state.selection as NodeSelection;
                        const selNode = sel.node;
                        if (selNode.isInline) {
                            const wrapped = inlineType.create(
                                { version },
                                selNode.content.size > 0 ? selNode.content : Fragment.from(selNode),
                            );
                            tr.replaceWith(from, to, wrapped);
                        } else {
                            const $from = doc.resolve(from);
                            const $to = doc.resolve(to);
                            const range = new NodeRange($from, $to, $from.depth);
                            const wrapping = findWrapping(range, blockType, { version });
                            if (!wrapping) return false;
                            tr.wrap(range, wrapping);
                        }
                        dispatch(tr);
                        return true;
                    }

                    const segments: BlockSegment[] = [];
                    doc.nodesBetween(from, to, (node, pos) => {
                        if (node.isBlock && !node.isTextblock) {
                            if (node.type.name === 'doc') return;

                            const nodeFrom = pos;
                            const nodeTo = pos + node.nodeSize;
                            const fullyContained = nodeFrom >= from && nodeTo <= to;

                            // Ensure listItem is only fully selected if the entire container is within the selection.
                            // Accounts for wrapper nodes in Tiptap's listItem structure.
                            if (node.type.name === 'listItem') {
                                const listItemFullyContained = nodeFrom + 2 >= from && nodeTo - 2 <= to;
                                segments.push({
                                    blockFrom: nodeFrom,
                                    blockTo: nodeTo,
                                    selFrom: nodeFrom,
                                    selTo: nodeTo,
                                    full: listItemFullyContained,
                                });
                            }

                            if (fullyContained) {
                                segments.push({
                                    blockFrom: nodeFrom,
                                    blockTo: nodeTo,
                                    selFrom: nodeFrom,
                                    selTo: nodeTo,
                                    full: true,
                                });
                                // Stop descending — the whole container is being wrapped.
                                return false;
                            }
                            // Partially overlapping container: continue descending so we
                            // can reach the textblocks (or further containers) inside it.
                            return;
                        }

                        if (!node.isTextblock) return;
                        const blockContentStart = pos + 1;
                        const blockContentEnd = pos + node.nodeSize - 1;

                        const selFrom = Math.max(from, blockContentStart);
                        const selTo = Math.min(to, blockContentEnd);
                        if (selFrom >= selTo) return;

                        const full = selFrom <= blockContentStart && selTo >= blockContentEnd;
                        segments.push({
                            blockFrom: pos,
                            blockTo: pos + node.nodeSize,
                            selFrom,
                            selTo,
                            full,
                        });
                    });

                    if (segments.length === 0) return false;
                    if (!dispatch) return true;

                    // Group consecutive fully-selected blocks
                    const actions: WrapAction[] = [];
                    let i = 0;
                    while (i < segments.length) {
                        const seg = segments[i];
                        if (seg.full) {
                            let groupEnd = i;
                            while (groupEnd + 1 < segments.length && segments[groupEnd + 1].full) {
                                groupEnd++;
                            }
                            actions.push({
                                type: 'block',
                                from: segments[i].blockFrom,
                                to: segments[groupEnd].blockTo,
                            });
                            i = groupEnd + 1;
                        } else {
                            actions.push({
                                type: 'inline',
                                from: seg.blockFrom,
                                to: seg.blockTo,
                                selFrom: seg.selFrom,
                                selTo: seg.selTo,
                            });
                            i++;
                        }
                    }

                    // Apply from end to start
                    for (let j = actions.length - 1; j >= 0; j--) {
                        const action = actions[j];
                        if (action.type === 'inline') {
                            const slice = doc.slice(action.selFrom!, action.selTo!);
                            const inlineContent = collectInlineContent(slice.content);
                            if (inlineContent.childCount === 0) continue;
                            const wrapped = inlineType.create({ version }, inlineContent);
                            tr.replaceWith(tr.mapping.map(action.selFrom!), tr.mapping.map(action.selTo!), wrapped);
                        } else {
                            const mappedFrom = tr.mapping.map(action.from);
                            const mappedTo = tr.mapping.map(action.to);
                            const $wrapFrom = tr.doc.resolve(mappedFrom);
                            const $wrapTo = tr.doc.resolve(mappedTo);
                            const range = $wrapFrom.blockRange($wrapTo);
                            if (!range) continue;
                            const wrapping = findWrapping(range, blockType, { version });
                            if (!wrapping) continue;
                            tr.wrap(range, wrapping);
                        }
                    }

                    dispatch(tr);
                    return true;
                },
        };
    },
});

/**
 * Collects all inline nodes from a fragment, including inline children of textblock nodes,
 * and returns a new fragment containing only these inline nodes.
 *
 * @param {Fragment} fragment - The ProseMirror fragment to process
 * @returns {Fragment} New fragment containing only the collected inline nodes
 */
function collectInlineContent(fragment: Fragment): Fragment {
    const nodes: PMNode[] = [];
    fragment.forEach(node => {
        if (node.isInline) {
            nodes.push(node);
        } else if (node.isTextblock) {
            node.content.forEach(child => nodes.push(child));
        }
    });
    return Fragment.from(nodes);
}

export { normalizeVersion };

/**
 * Determines the version to use for toggleOrInsertZyVersion commands.
 * Chooses the version from the following sources in order of precedence:
 *   1. attrs.version (if provided)
 *   2. First item from editor.storage.zyVersion?.versionFilter when it is allowed
 *   3. First entry in editor.storage.zyVersion?.allowedVersions
 *   4. Falls back to 'cpp' when no allowed list is configured.
 */
function getToggleOrInsertVersion(editor: Editor, attrs?: { version?: string }) {
    if (attrs?.version) {
        return normalizeVersion(attrs.version);
    }

    const allowedVersions = editor.storage.zyVersion?.allowedVersions ?? [];
    const selectedVersions = normalizeSelectedZyVersionFilter(getEditorVersionFilter(editor));
    const filteredSelection = selectedVersions?.find(token => allowedVersions.length === 0 || allowedVersions.includes(token));
    if (filteredSelection) {
        return filteredSelection;
    }
    if (allowedVersions.length > 0) {
        return allowedVersions[0];
    }

    return selectedVersions?.[0] ?? 'cpp';
}

export const ZyVersionInline = ZyVersionInlineHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyVersionInlineNodeView);
    },
});

export const ZyVersionBlock = ZyVersionBlockHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyVersionBlockNodeView);
    },
});
